import { Separator } from "@/components/ui/separator";
import { useSubscribeUser, useUserDetails } from "@/hooks";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { MXN_PER_POINT } from "@/lib/constants";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Zod schema for the discount form
const discountFormSchema = z.object({
  coupon: z.string().optional(),
});

type DiscountFormData = z.infer<typeof discountFormSchema>;

const BillingPage = () => {
  const { data: user } = useUserDetails();
  const { mutateAsync: subscribeUser } = useSubscribeUser();
  const [isLoading, setIsLoading] = useState(false);

  // Form for discount coupon
  const discountForm = useForm<DiscountFormData>({
    resolver: zodResolver(discountFormSchema),
    defaultValues: {
      coupon: "",
    },
  });

  if (user?.type === "anonymous") {
    return <Navigate to="/inicia-sesion?redirect=facturacion" />;
  }

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Facturación</h1>
          <p className="text-xs text-muted-foreground">
            Aquí puedes solicitar una factura para tus compras o comprar puntos
            para generar reportes detallados.
          </p>
        </div>
      </div>
      <Separator />
      <div className="w-full max-w-md mx-auto">
        <Card className="border-2 shadow-sm">
          <CardHeader className="text-left pb-1">
            <CardTitle className="text-2xl">Bernardino | Puntos</CardTitle>
            <CardDescription className="space-y-2">
              <div className="text-base font-medium text-foreground">
                ✨ Paga solo por lo que uses
              </div>
              <div className="text-xs">
                Sin cargos hasta generar tu reporte final. Crea reportes preliminares 
                ilimitados completamente <span className="font-semibold text-success">GRATIS</span> mientras 
                perfeccionas tu trabajo.
              </div>
            </CardDescription>
            <div className="mt-3 flex items-end justify-center">
              <span className="text-4xl font-bold">${MXN_PER_POINT} MXN</span>
              <span className="text-sm text-muted-foreground ml-1 mb-1">
                /punto
              </span>
            </div>
            <div className="text-center mt-1">
              <p className="text-xs text-muted-foreground">
                Solo se cobra cuando generes el reporte final
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-1">
            <ul className="space-y-3">
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Añade contraseñas a tus reportes PDF para mayor seguridad.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Ajusta los colores, diseño y límites ocupacionales de exposición de acuerdo a
                  tus necesidades.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Manten un registro de todos tus proyectos y reportes.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Aplicación android para capturar datos desde cualquier
                  dispositivo.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Comparte reportes con tus clientes a través de nuestro portal exclusivo y 
                  acceso instantáneo con enlace mágico, sin necesidad de crear cuenta. Revoca el acceso cuando quieras.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Lectura automática de formularios físicos con IA de última generación. 
                  Digitaliza tus datos de campo instantáneamente con precisión profesional.
                </span>
              </li>
              <li className="flex items-center">
                <Check className="mr-2 size-4 text-success shrink-0" />
                <span className="text-sm">
                  Editor avanzado de PDFs e imágenes para crear layouts personalizados 
                  en los apéndices de tus reportes. Herramientas profesionales integradas.
                </span>
              </li>
            </ul>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2 pt-2">
            {/* Discount Form */}
            <Form {...discountForm}>
              <form className="w-full space-y-2">
                <FormField
                  control={discountForm.control}
                  name="coupon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Clave Descuento <span className="text-xs text-muted-foreground ml-auto">(opcional)</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder=""
                          {...field}
                          className="text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>

            {isLoading ? (
              <Button
                size="lg"
                className="w-full bg-[#0074d4] text-white hover:bg-[#0074d4]/90"
              >
                <Loader2 className="mr-2 size-4 animate-spin" />
                Cargando...
              </Button>
            ) : (
              <Button
                size="lg"
                className="w-full bg-[#0074d4] text-white hover:bg-[#0074d4]/90"
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    // Get the coupon value from the form
                    const couponValue = discountForm.getValues("coupon");
                    await subscribeUser({ coupon: couponValue });
                    toast.success("Exito", {
                      description: "Se ha creado tu suscripción correctamente.",
                    });
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                Suscribir
              </Button>
            )}
            <p className="text-xs text-muted-foreground text-center mt-3">
              La factura se envía el día 25 de cada mes. Tienes 3 días para realizar el pago 
              antes de ser degradado al plan gratuito.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default BillingPage;
